import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { Collapse } from 'antd'
import { LegalDepositType } from 'types'
import DepositDetail from './DepositDetail'
import DepositEditForm from './DepositEditForm'

const { Panel } = Collapse

const DepositPanel = ({
  deposit,
  defaultOpen = false,
}: {
  deposit: LegalDepositType
  defaultOpen?: boolean
}) => {
  const [mode, setMode] = useState<'view' | 'form'>('view')
  const [showJson, setShowJson] = useState(false)

  const hasContent = deposit.content && Object.keys(deposit.content).length > 0

  const header = (
    <PanelHeader>
      <PanelTitle>
        Dépôt #{deposit.id}
        {deposit.status && <StatusBadge>{deposit.status}</StatusBadge>}
      </PanelTitle>
      <HeaderActions>
        {hasContent && (
          <JsonButton
            onClick={e => {
              e.stopPropagation()
              setShowJson(true)
            }}
          >
            {'{ } JSON'}
          </JsonButton>
        )}
        {mode === 'view' ? (
          <EditButton
            onClick={e => {
              e.stopPropagation()
              setMode('form')
            }}
          >
            Éditer
          </EditButton>
        ) : (
          <CancelButton
            onClick={e => {
              e.stopPropagation()
              setMode('view')
            }}
          >
            Annuler
          </CancelButton>
        )}
      </HeaderActions>
    </PanelHeader>
  )

  return (
    <>
      <StyledCollapse
        defaultActiveKey={defaultOpen ? [String(deposit.id)] : []}
      >
        <Panel key={deposit.id} header={header}>
          {mode === 'view' ? (
            <DepositDetail deposit={deposit} />
          ) : (
            <DepositEditForm
              deposit={deposit}
              onCancel={() => setMode('view')}
            />
          )}
        </Panel>
      </StyledCollapse>
      {showJson && (
        <JsonOverlay onClick={() => setShowJson(false)}>
          <JsonModal onClick={e => e.stopPropagation()}>
            <JsonModalHeader>
              <JsonModalTitle>Contenu JSON</JsonModalTitle>
              <JsonCloseButton onClick={() => setShowJson(false)}>
                ✕
              </JsonCloseButton>
            </JsonModalHeader>
            <JsonPre>{JSON.stringify(deposit.content, null, 2)}</JsonPre>
          </JsonModal>
        </JsonOverlay>
      )}
    </>
  )
}

export default DepositPanel

// ─── Styles ───────────────────────────────────────────────────────────────────

const StyledCollapse = styled(Collapse)`
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-right: 8px;
`

const PanelTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 15px;
`

const StatusBadge = styled.span`
  background: #f2f3f5;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 500;
  color: #555;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const baseButtonStyles = css`
  border: none;
  border-radius: 6px;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
`

const JsonButton = styled.button`
  font-size: 11px;
  font-weight: 600;
  color: #1677ff;
  background: #f0f5ff;
  border: 1px solid #adc6ff;
  border-radius: 4px;
  padding: 2px 8px;
  cursor: pointer;
  line-height: 1.4;
  &:hover {
    background: #d6e4ff;
  }
`

const EditButton = styled.button`
  ${baseButtonStyles}
  background: #e6f4ff;
  color: #1677ff;

  &:hover {
    background: #bae0ff;
  }
`

const CancelButton = styled.button`
  ${baseButtonStyles}
  background: #fff1f0;
  color: #ff4d4f;

  &:hover {
    background: #ffd8d8;
  }
`

const JsonOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`

const JsonModal = styled.div`
  background: #1e1e2e;
  border-radius: 8px;
  width: 700px;
  max-width: 95vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
`

const JsonModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: #2a2a3e;
  border-bottom: 1px solid #3a3a55;
`

const JsonModalTitle = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #cdd6f4;
`

const JsonCloseButton = styled.button`
  background: none;
  border: none;
  color: #cdd6f4;
  font-size: 16px;
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
  &:hover {
    color: #ff5555;
  }
`

const JsonPre = styled.pre`
  margin: 0;
  padding: 16px;
  overflow: auto;
  font-size: 12px;
  font-family: 'Fira Code', 'Cascadia Code', monospace;
  color: #a6e3a1;
  line-height: 1.6;
  flex: 1;
`
